<template>
  <div class="google-auth-container">
    <div v-if="isGoogleAuthEnabled">
      <GoogleLogin
        :client-id="googleClientId"
        :callback="handleGoogleSuccess"
        :error="handleGoogleError"
        :button-config="{
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left'
        }"
      />
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
    <div v-else class="google-auth-disabled">
      <!-- Google Auth is disabled -->
    </div>
  </div>
</template>

<script>
import { GoogleLogin } from 'vue3-google-login'
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import axios from 'axios'

export default {
  name: 'GoogleAuth',
  components: {
    GoogleLogin
  },
  props: {
    mode: {
      type: String,
      default: 'login', // 'login' or 'register'
      validator: (value) => ['login', 'register'].includes(value)
    }
  },
  emits: ['success', 'error'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const router = useRouter()

    const errorMessage = ref('')
    const googleClientId = import.meta.env.VITE_GOOGLE_AUTH_CLIENT_ID
    const googleAuthCookie = import.meta.env.VITE_GOOGLE_AUTH_HIDDEN_BY_COOKIE

    // Check if Google Auth is enabled by checking if client ID is provided
    // and if the cookie indicates it's enabled
    const isGoogleAuthEnabled = computed(() => {
      if (!googleClientId) return false

      // Check if there's a cookie that controls Google auth visibility
      if (googleAuthCookie) {
        // Check if the cookie exists and is set to enable Google auth
        const cookies = document.cookie.split(';')
        const authCookie = cookies.find(cookie =>
          cookie.trim().startsWith(`${googleAuthCookie}=`)
        )

        if (authCookie) {
          const cookieValue = authCookie.split('=')[1]
          return cookieValue === 'true' || cookieValue === '1'
        }

        // If cookie doesn't exist, default to enabled if client ID is present
        return true
      }

      return true
    })

    const handleGoogleSuccess = async (response) => {
      try {
        errorMessage.value = ''

        // Extract the credential (JWT token) from Google's response
        const { credential } = response

        if (!credential) {
          throw new Error('No credential received from Google')
        }

        // Send the Google ID token to backend for verification and user creation/login
        const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/client/auth/google/login/`
        const backendResponse = await axios.post(apiUrl, {
          id_token: credential
        })

        if (backendResponse.data.access) {
          // Store the JWT token in Vuex store
          store.dispatch('login', { token: backendResponse.data.access })

          // Emit success event to parent component
          emit('success', {
            user: backendResponse.data.user,
            token: backendResponse.data.access,
            mode: props.mode
          })

          // Redirect to dashboard
          router.push({ name: 'PlayerHomeDashboard' })
        } else {
          throw new Error('No access token received from backend')
        }

      } catch (error) {
        console.error('Google auth error:', error)

        let errorMsg = t('auth.googleAuthError')

        if (error.response) {
          // Backend returned an error
          if (error.response.status === 400) {
            errorMsg = error.response.data.message || t('auth.invalidGoogleToken')
          } else if (error.response.status === 409) {
            errorMsg = t('auth.userAlreadyExists')
          } else {
            errorMsg = t('auth.serverError')
          }
        } else if (error.message) {
          errorMsg = error.message
        }

        errorMessage.value = errorMsg
        emit('error', { error: errorMsg, mode: props.mode })
      }
    }

    const handleGoogleError = (error) => {
      console.error('Google login error:', error)
      const errorMsg = t('auth.googleLoginFailed')
      errorMessage.value = errorMsg
      emit('error', { error: errorMsg, mode: props.mode })
    }

    return {
      errorMessage,
      googleClientId,
      isGoogleAuthEnabled,
      handleGoogleSuccess,
      handleGoogleError
    }
  }
}
</script>

<style scoped>
.google-auth-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
}

.google-auth-container > div {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.error-message {
  width: 100%;
  max-width: 100%;
  color: #ff8a80;
  background: rgba(41, 41, 41, 0.8);
  border: 1px solid rgba(255, 138, 128, 0.4);
  border-radius: 4px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5);
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
  text-align: center;
}

.google-auth-disabled {
  display: none;
}

:deep(iframe) {
  color-scheme: light;
  max-width: 100%;
}

:deep(div[role="button"]) {
  border-radius: 4px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}
</style>